'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';

import { Link as IntlLink } from '@/i18n/navigation';

import { SceneCanvas } from './SceneCanvas';

interface ProductMeta {
	id: 'kyc' | 'kyb' | 'crypto';
	sys: string;
	tag: string;
	short: string;
	code: string;
	scene: string;
	kicker: string;
	// Dedicated product page — present once built; the CTA links there. KYB/AML
	// pages arrive next, so their CTA stays a plain button until then.
	route?: string;
}

// Structural data (HUD codes / scene ids stay literal); copy comes from i18n.
const PRODUCTS: readonly ProductMeta[] = [
	{
		id: 'kyc',
		sys: '01',
		tag: 'KYC',
		short: 'KYC',
		code: 'ID-01',
		scene: 'kyc-02',
		kicker: 'IDENTITY VERIFICATION',
		route: '/products/kyc',
	},
	{
		id: 'kyb',
		sys: '02',
		tag: 'KYB',
		short: 'KYB',
		code: 'BIZ-02',
		scene: 'biz-tree',
		kicker: 'BUSINESS VERIFICATION',
		route: '/products/kyb',
	},
	{
		id: 'crypto',
		sys: '03',
		tag: 'CRYPTO',
		short: 'AML CRYPTO',
		code: 'CHAIN-03',
		scene: 'kyb-04',
		kicker: 'ON-CHAIN AML',
		route: '/products/aml',
	},
];

/**
 * Products deck — an accordion of three products (KYC / KYB / Crypto AML).
 * The active panel expands; collapsed panels show a compact spine (vertical on
 * desktop, a bar on mobile) and expand on click. Desktop ↔ mobile layout is
 * pure CSS at the prototype's 820px breakpoint, so no resize state is needed.
 *
 * Cards are the INVERSE of the page (fg/bg tokens): dark on the light theme,
 * light on the dark theme, so they stay legible against the page in both. The
 * canvas scene inverts and the gradient veil flips via --card-veil — see
 * globals.css (.products-scene / .products-veil-*).
 */
export function ProductsSection() {
	const t = useTranslations('Products');
	const [active, setActive] = useState(0);

	// Same single persistent element per card whose `flex` / `min-height`
	// transition on switch — matching the prototype (flex .45s + min-height .45s).
	const panelBase =
		'relative overflow-hidden border border-fg text-bg transition-[flex,min-height] duration-[450ms] ease-[cubic-bezier(.4,0,.1,1)] min-[820px]:min-w-[78px]';

	return (
		<section
			id="products"
			className="relative scroll-mt-36 py-16 sm:scroll-mt-44 sm:py-24"
		>
			{/* Section header */}
			<div className="mb-5 sm:mb-6">
				<div className="label-mono">SECTION_01 // PRODUCTS</div>
				<div className="mt-2 flex flex-wrap items-end justify-between gap-x-5 gap-y-3">
					<h2 className="text-[clamp(28px,4.4vw,50px)] leading-[1.02] font-normal tracking-[-0.025em]">
						{t('title1')}
						<br />
						{t('title2')}
					</h2>
					<div className="label-mono pb-1 whitespace-nowrap">
						SELECT_PRODUCT&nbsp;//&nbsp;{PRODUCTS[active].code}
					</div>
				</div>
				<div className="bg-border mt-[18px] h-px" />
			</div>

			{/* Deck */}
			<div className="flex w-full flex-col gap-[6px] min-[820px]:h-[clamp(440px,52vw,580px)] min-[820px]:flex-row">
				{PRODUCTS.map((p, i) => {
					const isActive = i === active;
					const title = t(`${p.id}.title`);
					const caps = t.raw(`${p.id}.caps`) as string[];
					const stats = t.raw(`${p.id}.stats`) as [string, string][];
					const isCrypto = p.tag === 'CRYPTO';
					const sizing = isActive
						? 'flex-none min-[820px]:flex-[3.2_1_0%] max-[819px]:min-h-[440px]'
						: 'flex-none min-[820px]:flex-[0.6_1_0%] max-[819px]:min-h-[60px] cursor-pointer hover:border-bg/30';

					return (
						<div key={p.id} className={`${panelBase} ${sizing} bg-fg`}>
							<SceneCanvas
								scene={p.scene}
								className={`products-scene absolute inset-0 h-full w-full ${isActive ? 'opacity-50' : 'opacity-[0.32]'}`}
							/>
							<div
								className={`pointer-events-none absolute inset-0 ${isActive ? 'products-veil-active' : 'products-veil-collapsed'}`}
							/>

							{!isActive && (
								<>
									{/* Collapsed — desktop spine */}
									<span
										aria-hidden="true"
										className="relative z-[1] hidden h-full flex-col items-center justify-between py-[22px] min-[820px]:flex"
									>
										<span className="text-bg/72 font-mono text-[12px] tracking-[1px]">
											{p.sys}
										</span>
										<span className="text-bg [transform:rotate(180deg)] font-mono text-[14px] tracking-[3px] uppercase [writing-mode:vertical-rl]">
											{p.short}
										</span>
										<span className="border-bg/40 text-bg flex h-[26px] w-[26px] items-center justify-center border font-mono text-[14px]">
											+
										</span>
									</span>
									{/* Collapsed — mobile bar */}
									<span
										aria-hidden="true"
										className="relative z-[1] flex h-full items-center justify-between gap-3 px-[18px] py-[18px] min-[820px]:hidden"
									>
										<span className="text-bg flex items-center gap-3 font-mono tracking-[1.5px]">
											<span className="text-bg/72 text-[11px]">{p.sys}</span>
											<span className="text-[13px]">{p.short}</span>
										</span>
										<span className="border-bg/40 flex h-6 w-6 items-center justify-center border text-[13px]">
											+
										</span>
									</span>
									{/* Full-area click target — keeps the panel one persistent
									    element so `flex` animates smoothly on switch. */}
									<button
										type="button"
										onClick={() => setActive(i)}
										aria-label={title}
										className="absolute inset-0 z-[2] cursor-pointer"
									/>
								</>
							)}

							{isActive && (
								<div className="relative z-[1] flex h-full flex-col p-[clamp(20px,2.4vw,32px)]">
									<div className="text-bg/72 flex items-center justify-between font-mono text-[11px] tracking-[1.5px]">
										<span>SYS.{p.sys}</span>
										<span className={isCrypto ? 'text-bg font-bold' : ''}>
											[ {p.tag} ]
										</span>
									</div>
									<div className="mt-auto">
										<div className="text-bg/72 font-mono text-[11px] tracking-[2px]">
											{p.kicker}
										</div>
										<h3 className="text-bg mt-2.5 text-[clamp(22px,2.6vw,30px)] leading-[1.05] font-medium tracking-[-0.02em]">
											{title}
										</h3>
										<p className="text-bg/75 mt-3.5 max-w-[46ch] text-[14px] leading-[1.6]">
											{t(`${p.id}.desc`)}
										</p>
										<div className="mt-5 flex flex-wrap gap-2">
											{caps.map((cap) => (
												<span
													key={cap}
													className="border-bg/20 text-bg/80 border px-2.5 py-1.5 font-mono text-[10.5px] tracking-[0.5px]"
												>
													{cap}
												</span>
											))}
										</div>
										<div className="border-bg/15 mt-6 flex flex-wrap items-end justify-between gap-4 border-t pt-[18px]">
											<div className="flex gap-[26px]">
												{stats.map(([label, value]) => (
													<span key={label} className="flex flex-col gap-[3px]">
														<span className="text-bg font-mono text-[17px] font-medium">
															{value}
														</span>
														<span className="text-bg/72 font-mono text-[9px] tracking-[1.5px] uppercase">
															{label}
														</span>
													</span>
												))}
											</div>
											{p.route ? (
												<IntlLink
													href={p.route}
													className="bg-bg text-fg flex cursor-crosshair items-center gap-3 px-[18px] py-[13px] font-mono text-[11px] tracking-[1.5px] no-underline transition-opacity hover:opacity-85"
												>
													{t('cta')}
													<span aria-hidden="true">-&gt;</span>
												</IntlLink>
											) : (
												<button
													type="button"
													className="bg-bg text-fg flex cursor-crosshair items-center gap-3 px-[18px] py-[13px] font-mono text-[11px] tracking-[1.5px] transition-opacity hover:opacity-85"
												>
													{t('cta')}
													<span aria-hidden="true">-&gt;</span>
												</button>
											)}
										</div>
									</div>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</section>
	);
}
